import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { resolverPortalDeLaUrl } from './tenant-basename';
import { RUTAS_PUBLICAS_RETIRADAS } from './rutas-retiradas';
import { PortalGuard } from './portal-guard';
import { Suspense, lazy, useEffect, useState, type ComponentType } from 'react';
import { RAIZ_DE_LA_EMPRESA, destinoDeLaRaiz } from './entrada';
import { EntradaALaPrimeraPantalla } from './primera-pantalla';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { ultimoPortalActivo } from '@/lib/utils/portal-storage';
import { MarcoDeLaApp } from '@/components/layout/marco-de-la-app';
import { AppLayout } from '@/components/layout/app-layout';
import { Skeleton } from '@/components/ui/skeleton';
import { ProtectedRoute, RequireRole } from './protected-route';
import { UserRole } from '@/types';

// Landing
import { LandingPage } from '@/features/landing/pages/landing-page';

// Auth pages
import { LoginPage } from '@/features/auth/pages/login-page';
import { PortalPickerPage } from '@/features/auth/pages/portal-picker-page';
import { RegisterPage } from '@/features/auth/pages/register-page';
import { InvitePage } from '@/features/auth/pages/invite-page';
import { ForgotPasswordPage } from '@/features/auth/pages/forgot-password-page';
import { ResetPasswordPage } from '@/features/auth/pages/reset-password-page';

// Public pages — Client portal (sin login, por token)
import { PublicQuoteTrackingPage } from '@/features/public-tracking/pages/public-quote-tracking-page';

// Public pages — Resources & Case Studies
import { ResourcesPage } from '@/features/resources/pages/resources-page';

// Public pages — Legal
import { TermsPage } from '@/features/legal/pages/terms-page';
import { PrivacyPage } from '@/features/legal/pages/privacy-page';
import { ContractPage } from '@/features/legal/pages/contract-page';
import { LegalNoticePage } from '@/features/legal/pages/legal-notice-page';
import { AcceptableUsePage } from '@/features/legal/pages/acceptable-use-page';
import { DataProcessingPage } from '@/features/legal/pages/data-processing-page';

// Public pages — Brand
import { WhyPrixiaPage } from '@/features/brand/pages/why-prixia-page';

// Public pages — Institutional
import { AboutPage } from '@/features/about/pages/about-page';
import { ContactPage } from '@/features/contact/pages/contact-page';

// Video presentation
import { VideoPage } from '@/features/video/pages/video-page';

// Sales PDF
import { SalesPDFPage } from '@/features/sales-pdf/pages/sales-pdf-page';

// Public pages — Platform & Solutions
import { PlatformPage } from '@/features/platform/pages/platform-page';

// Public pages — Go-To-Market

// Public pages — Docs
import { ProductGuidePage } from '@/features/docs/pages/product-guide-page';
import { ImplementationPage } from '@/features/docs/pages/implementation-page';
import { IntegrationsPage } from '@/features/docs/pages/integrations-page';
import { RoiPage } from '@/features/docs/pages/roi-page';
import { HelpPage } from '@/features/docs/pages/help-page';
import { QuickStartPage } from '@/features/docs/pages/quick-start-page';
import { FaqPage } from '@/features/docs/pages/faq-page';
import { PricingStrategyPage } from '@/features/docs/pages/pricing-strategy-page';

// App pages
import { EntradaDeInicio } from '@/features/mini-prixia/components/entrada-de-inicio';
import { DashboardPage } from '@/features/dashboard/pages/dashboard-page';
import { InicioPage } from '@/features/inicio/pages/inicio-page';
import { MasterDataPage } from '@/features/master-data/pages/master-data-page';
import { TariffAssistantPage } from '@/features/tariff-assistant/pages/tariff-assistant-page';
import { ModelsListPage } from '@/features/pricing-models/pages/models-list-page';
import { ModelDetailPage } from '@/features/pricing-models/pages/model-detail-page';
import { QuotesListPage } from '@/features/quotes/pages/quotes-list-page';
import { QuoteCreatePage } from '@/features/quotes/pages/quote-create-page';
import { QuoteDetailPage } from '@/features/quotes/pages/quote-detail-page';
import { ApprovalsPage } from '@/features/approvals/pages/approvals-page';
import { TutorialPage } from '@/features/tutorial/pages/tutorial-page';
import { HelpCenterPage } from '@/features/help-center/pages/help-center-page';
import CursoImplementacionPage from '@/features/help-center/pages/curso-implementacion-page';
import EquipoDetailPage from '@/features/organizations/pages/equipo-detail-page';
import { CustomersPage } from '@/features/customers/pages/customers-page';
import { ProjectStatusPage } from '@/features/project-status/pages/project-status-page';
import { ProfilePage } from '@/features/profile/pages/profile-page';
import { SettingsPage } from '@/features/settings/pages/settings-page';
import { MiEmpresaPage } from '@/features/mi-empresa/pages/mi-empresa-page';
import { ApiDocsPage } from '@/features/developer/pages/api-docs-page';
import { AnalyticsPage } from '@/features/analytics/pages/analytics-page';
import { AiIntelligencePage } from '@/features/ai-intelligence/pages/ai-intelligence-page';
import { AiProductivityPage } from '@/features/ai-productivity/pages/ai-productivity-page';
import { RedirigirPreciosDinamicos } from '@/features/dynamic-pricing/compite-comun/redirigir-precios-dinamicos';
import { TemaCompite } from '@/components/tema/tema-compite';
import { IdentificacionPage } from '@/features/dynamic-pricing/pages/identificacion-page';
import { MaterialsPage } from '@/features/materials/pages/materials-page';
import { ImportOrdersPage } from '@/features/import-tracking/pages/import-orders-page';
import { ImportOrderDetailPage } from '@/features/import-tracking/pages/import-order-detail-page';
import { ContainerDetailPage } from '@/features/import-tracking/pages/container-detail-page';

// Trade Intelligence — preventa
import { CotizadorPage } from '@/features/trade/pages/cotizador-page';
import { ComerciaProductosPage } from '@/features/comercia/pages/productos-page';
import { OrigenDestinoPage } from '@/features/comercia/pages/origen-destino-page';
import { CargaPage } from '@/features/comercia/pages/carga-page';
import { RutasPage } from '@/features/comercia/pages/rutas-page';
import { CostoNacionalizadoPage } from '@/features/comercia/pages/costo-nacionalizado-page';
import { GestionInicio } from '@/features/cotiza/pages/gestion-inicio';
import { TarifarioPage } from '@/features/trade/pages/tarifario-page';
import {
  LegacyImportRedirect,
  LegacyOrderRedirect,
  LegacyContainerRedirect,
  BandejaDatecRedirect,
} from './legacy-redirects';
import SmartPricingPage from '@/features/smart-pricing/pages/smart-pricing-page';
import DecisionesPage from '@/features/compite/pages/decisiones-page';
import RevisarPreciosPage from '@/features/compite/pages/revisar-precios-page';
import EstrategiaPage from '@/features/compite/pages/estrategia-page';
import { direccionNuevaDe, hrefsDeCompite } from '@/features/compite/lib/rutas';
import ApiKeysPage from '@/features/api-keys/pages/api-keys-page';
import { IntegrationPage } from '@/features/api-keys/pages/integration-page';
import StoreConnectionsPage from '@/features/store-connections/pages/store-connections-page';
import { IntegrationsPage as IntegrationsAdminPage } from '@/features/integrations/pages/integrations-page';
import { ContractsListPage } from '@/features/contracts/pages/contracts-list-page';
import { ContractDetailPage } from '@/features/contracts/pages/contract-detail-page';
import { ContractEditPage } from '@/features/contracts/pages/contract-edit-page';
import PushHistoryPage from '@/features/store-connections/pages/push-history-page';

// Pricer Datec (módulo paralelo)
import { EDITOR_NUEVO } from '@/features/pricer-datec/lib/interruptor-editor';
import { rutasDelPricerDatec } from './rutas-del-pricer-datec';
import { PricerDatecListPage } from '@/features/pricer-datec/pages/pricer-datec-list-page';
import { PricerDatecAjustesPage } from '@/features/pricer-datec/pages/pricer-datec-ajustes-page';

// Oportunidades HubSpot
import { OpportunitiesListPage } from '@/features/opportunities/pages/opportunities-list.page';

// Pricer Solar (cotizador fotovoltaico On-Grid)
import { PricerSolarPage } from '@/features/pricer-solar/pages/pricer-solar-page';
import { PricerSolarListPage } from '@/features/pricer-solar/pages/pricer-solar-list-page';
import { PricerSolarConfigPage } from '@/features/pricer-solar/pages/pricer-solar-config-page';

// Admin (Super Admin)
import { AdminDashboardPage } from '@/features/admin/pages/admin-dashboard-page';
import { AdminTenantsPage } from '@/features/admin/pages/admin-tenants-page';
import { AdminComparacionPage } from '@/features/admin/pages/admin-comparacion-page';
import { AdminTenantDetailPage } from '@/features/admin/pages/admin-tenant-detail-page';
import { AdminTiendasPrixiaPage } from '@/features/admin/pages/admin-tiendas-prixia-page';
import { AdminTiendaPrixiaPage } from '@/features/admin/pages/admin-tienda-prixia-page';
import { AdminLecturasPrixiaPage } from '@/features/admin/pages/admin-lecturas-prixia-page';
import { AdminInvitacionesDemoPage } from '@/features/admin/pages/admin-invitaciones-demo-page';
import { SolicitudesPage } from '@/features/solicitudes/pages/solicitudes-page';
import { AdminPersonasPage } from '@/features/admin/pages/admin-personas-page';
import { AdminUserDetailPage } from '@/features/admin/pages/admin-user-detail-page';
import { AdminPlansPage } from '@/features/admin/pages/admin-plans-page';
import { AdminPlatformSettingsPage } from '@/features/admin/pages/admin-platform-settings-page';
import { AdminBugReportsPage } from '@/features/admin/pages/admin-bug-reports-page';
import { FueraDeLaDemo } from '@/features/mini-prixia/components/fuera-de-la-demo';

// Mini Prixia: la invitación de EGX One.
const InvitacionPage = lazy(() => import('@/features/mini-prixia/pages/invitacion-page'));

// ── Animated route wrapper ───────────────────────────────────────

/**
 * Redirige a `/tariff-assistant` **con el query string intacto**.
 *
 * `<Navigate to="/tariff-assistant">` a secas lo tira: un enlace compartido a
 * `/trade/aduana?partida=8415.10.10.00` aterrizaría en el buscador vacío, y el
 * que lo abrió tendría que buscar de nuevo la partida que ya venía escrita.
 */
function RedirigirAlAsistente() {
  const { search } = useLocation();
  return <Navigate to={`/tariff-assistant${search}`} replace />;
}

/**
 * `/pricing-rules` → Compite › Estrategia › Reglas, y
 * `/pricing-rules?ver=sin-costo` → Compite › Estrategia › Productos (sin costo).
 *
 * Reglas es parte de Estrategia desde el plan 3; desde el rediseño del 14-oct,
 * Estrategia es su propia pantalla. «Cómo está cada producto» es la pestaña
 * Productos (ruling del 25-sep). Un enlace guardado no puede aterrizar en otro
 * lado: la traducción la hace `direccionNuevaDe`, la misma de `/precio-inteligente`.
 */
function RedirigirAReglas() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const ver = params.get('ver');
  params.set('destino', ver === 'sin-costo' || ver === 'productos' ? 'productos' : 'reglas');
  if (ver === 'productos') params.delete('ver');
  return <Navigate to={direccionNuevaDe(params) ?? hrefsDeCompite.reglas()} replace />;
}

/**
 * `/precio-inteligente` (Estrategias de Mercado) se partió en Compite ›
 * Decisiones y Compite › Estrategia (rediseño del 14-oct). Cada destino viejo
 * va a su pantalla nueva conservando `?vuelta=`, `?producto=` y `?ver=sin-costo`.
 * Los destinos detrás de una bandera interna (Análisis, Insights, Informe IA,
 * Diagnóstico y Vinculados) no se mudaron: siguen abriendo la página de antes.
 */
function PrecioInteligenteORedirigir() {
  const { search } = useLocation();
  const nueva = direccionNuevaDe(new URLSearchParams(search));
  if (nueva) return <Navigate to={nueva} replace />;
  return <SmartPricingPage />;
}

/**
 * Redirige una dirección vieja que lleva `:id` a su equivalente nueva.
 *
 * `<Navigate to="/plataforma/empresas/:id">` navegaría literalmente a la ruta
 * con los dos puntos: `Navigate` no interpola parámetros. Hay que leerlos.
 */
function RedirigirConId({ a }: { a: string }) {
  const { id } = useParams();
  return <Navigate to={`${a}/${id}`} replace />;
}

/**
 * El catálogo de componentes (`/_ui`, spec 310 decisión D-4). Con
 * `import.meta.env.DEV` en falso, el build reemplaza esto por `null` y el
 * `import()` desaparece: no llega a producción ni como chunk suelto.
 */
/**
 * Una pantalla con carga diferida que además se puede precargar: una vez bajada,
 * se dibuja directo, sin pasar por el `Suspense` (ni un cuadro de espera).
 */
function diferidaConPrecarga(cargar: () => Promise<{ default: ComponentType }>) {
  let lista: ComponentType | null = null;
  let pedido: Promise<{ default: ComponentType }> | null = null;
  const precargar = () =>
    (pedido ??= cargar().then((m) => {
      lista = m.default;
      return m;
    }));
  const Diferida = lazy(precargar);
  function Pantalla() {
    const Lista = lista;
    return Lista ? <Lista /> : <Diferida />;
  }
  return Object.assign(Pantalla, { precargar });
}

/** Compite › Monitoreo, Productos, Competencia y Análisis, con carga diferida. */
const MonitoreoPage = diferidaConPrecarga(
  () => import('@/features/dynamic-pricing/pages/monitoreo-page')
);
const ProductosPage = diferidaConPrecarga(
  () => import('@/features/dynamic-pricing/pages/productos-page')
);
const CompetenciaPage = diferidaConPrecarga(
  () => import('@/features/dynamic-pricing/pages/competencia-page')
);
const AnaliticaPage = diferidaConPrecarga(
  () => import('@/features/dynamic-pricing/pages/analitica-page')
);

/**
 * Mientras llega una pantalla que se carga aparte: su esqueleto ADENTRO del
 * marco. Antes era `fallback={null}` y, como cada pantalla traía su propio menú,
 * se veía todo en blanco casi un segundo (pedido de Joaquín, 16-oct).
 */
function CargandoLaPantalla() {
  return (
    <AppLayout>
      <div
        className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8"
        aria-busy="true"
        aria-label="Cargando la pantalla"
      >
        <Skeleton className="h-6 w-48 rounded-full" />
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    </AppLayout>
  );
}

/**
 * El portal público de seguimiento de importaciones arrastra `three` (el mapa 3D): con carga diferida
 * deja de viajar en el paquete inicial de todas las pantallas. Falta medir el tamaño del paquete.
 */
const PublicImportTrackingPage = lazy(() =>
  import('@/features/public-tracking/pages/public-import-tracking-page').then((m) => ({
    default: m.PublicImportTrackingPage,
  }))
);

const CatalogoUiPage = import.meta.env.DEV
  ? lazy(() => import('@/features/catalogo-ui/pages/catalogo-ui-page'))
  : null;

function AnimatedRoutes({ enEmpresa }: { enEmpresa: boolean }) {
  /*
    🔴 Acá había un fundido de la pantalla ENTERA atado a la dirección
    (`AnimatePresence` con `key={location.pathname}`): al navegar se apagaba todo,
    menú incluido, y convivían dos pantallas 150 ms. Ahora el menú y la barra
    son un marco fijo (`MarcoDeLaApp`) y sólo el contenido se funde, adentro.
  */
  useEffect(() => {
    // Las pantallas de Compite se bajan aparte; se precargan cuando la app ya
    // está quieta, así el primer clic no espera.
    const t = window.setTimeout(() => {
      for (const p of [MonitoreoPage, ProductosPage, CompetenciaPage, AnaliticaPage]) {
        void p.precargar().catch(() => {});
      }
    }, 1500);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <MarcoDeLaApp>
      <Routes>
        {/* Public — Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/portales" element={<PortalPickerPage />} />
        {CatalogoUiPage && (
          <Route
            path="/_ui"
            element={
              <Suspense fallback={null}>
                <CatalogoUiPage />
              </Suspense>
            }
          />
        )}

        {/* Public — Portal cliente (sin login, por token) */}
        <Route
          path="/track/import/:token"
          element={
            <Suspense fallback={null}>
              <PublicImportTrackingPage />
            </Suspense>
          }
        />
        <Route path="/track/quote/:token" element={<PublicQuoteTrackingPage />} />
        <Route path="/register" element={<RegisterPage />} />
        {/* Mini Prixia: la invitación de EGX One (ticket → alta → la app). */}
        <Route
          path="/invitacion/:codigo"
          element={
            <Suspense fallback={null}>
              <InvitacionPage />
            </Suspense>
          }
        />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Public — Platform & Solutions */}
        <Route path="/platform" element={<PlatformPage />} />

        {/* Public — Resources & Case Studies */}
        <Route path="/resources" element={<ResourcesPage />} />

        {/* Public — Legal */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contract" element={<ContractPage />} />
        <Route path="/legal-notice" element={<LegalNoticePage />} />
        <Route path="/acceptable-use" element={<AcceptableUsePage />} />
        <Route path="/data-processing" element={<DataProcessingPage />} />

        {/* Public — Brand */}
        <Route path="/why-prixia" element={<WhyPrixiaPage />} />

        {/* Public — Institutional */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/video" element={<VideoPage />} />
        <Route path="/sales-pdf" element={<SalesPDFPage />} />

        {/*
          Landing nueva (vault 346, L0): páginas públicas retiradas. GTM y roadmap exponían estrategia
          interna; blog, casos, empleo y rubros eran contenido inventado (D20, D25). La ruta y el import
          se borran (si no, el texto viaja en el paquete JS de cualquier visitante) y la dirección vieja
          lleva al inicio. La lista vive en RUTAS_PUBLICAS_RETIRADAS y la cuida un test.
        */}
        {RUTAS_PUBLICAS_RETIRADAS.map((ruta) => (
          <Route key={ruta} path={ruta} element={<Navigate to="/" replace />} />
        ))}

        {/* Public — Docs */}
        <Route path="/docs/product-guide" element={<ProductGuidePage />} />
        <Route path="/docs/implementation" element={<ImplementationPage />} />
        <Route path="/docs/integrations" element={<IntegrationsPage />} />
        <Route path="/docs/roi" element={<RoiPage />} />
        <Route path="/docs/help" element={<HelpPage />} />
        <Route path="/docs/quick-start" element={<QuickStartPage />} />
        <Route path="/docs/faq" element={<FaqPage />} />
        <Route path="/docs/pricing-strategy" element={<PricingStrategyPage />} />

        {/* Protected */}
        <Route
          path="/dashboard"
          element={
            <EntradaDeInicio>
              {/* Recorrido 3-nov: sin el Inicio, a la primera pantalla que la empresa abre. */}
              <EntradaALaPrimeraPantalla>
                <ProtectedRoute>
                  {/* 18-oct (artefacto): Inicio es el resumen de los tres módulos. */}
                  <InicioPage />
                </ProtectedRoute>
              </EntradaALaPrimeraPantalla>
            </EntradaDeInicio>
          }
        />
        {/*
          El Dashboard de cotizaciones de siempre (indicadores, tendencia,
          pronóstico, clientes, márgenes, embudo…) es Cotiza › Visión general,
          como en el artefacto. Mismo permiso que antes (`dashboard`).
        */}
        <Route path="/cotiza" element={<Navigate to="/cotiza/vision-general" replace />} />
        {/* Cotiza › Gestión (artefacto, 19-oct): lleva al paso que la persona puede abrir. */}
        <Route
          path="/cotiza/gestion"
          element={
            <ProtectedRoute>
              <GestionInicio />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cotiza/vision-general"
          element={
            <ProtectedRoute menuKey="dashboard">
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        {/* La ficha viaja en ?partida= (query string, no pathname) — ver el
              comentario en TariffAssistantPage: el wrapper animado remonta
              por pathname y un pathname por ficha perdía la búsqueda. */}
        <Route
          path="/tariff-assistant"
          element={
            <ProtectedRoute>
              <TariffAssistantPage />
            </ProtectedRoute>
          }
        />
        {/* El catálogo arancelario se mudó a su propia sección (2026-08-09).
              La URL vieja redirige para no romper favoritos ni links pegados. */}
        <Route
          path="/master-data/tariff-catalog"
          element={<Navigate to="/tariff-assistant" replace />}
        />
        <Route
          path="/master-data"
          element={
            <ProtectedRoute>
              <MasterDataPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/master-data/:tab"
          element={
            <ProtectedRoute>
              <MasterDataPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricing-models"
          element={
            <ProtectedRoute>
              <FueraDeLaDemo>
                <ModelsListPage />
              </FueraDeLaDemo>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricing-models/:id"
          element={
            <ProtectedRoute>
              <FueraDeLaDemo>
                <ModelDetailPage />
              </FueraDeLaDemo>
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotes"
          element={
            <ProtectedRoute>
              <QuotesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotes/new"
          element={
            <ProtectedRoute>
              <QuoteCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotes/:id"
          element={
            <ProtectedRoute>
              <QuoteDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotes/:id/edit"
          element={
            <ProtectedRoute>
              <QuoteCreatePage />
            </ProtectedRoute>
          }
        />

        {/* Pricer Datec — módulo paralelo */}
        <Route
          path="/pricer-datec"
          element={
            <ProtectedRoute>
              <PricerDatecListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricer-datec/quotes"
          element={
            <ProtectedRoute>
              <PricerDatecListPage />
            </ProtectedRoute>
          }
        />
        {/* F5: el editor nuevo es LA pantalla del Pricer (el interruptor es el freno de emergencia). */}
        {rutasDelPricerDatec(EDITOR_NUEVO)}
        <Route path="/pricer-datec/approvals" element={<BandejaDatecRedirect />} />
        <Route
          path="/pricer-datec/configuracion"
          element={
            <ProtectedRoute>
              <PricerDatecAjustesPage />
            </ProtectedRoute>
          }
        />

        {/* Oportunidades HubSpot */}
        <Route
          path="/opportunities"
          element={
            <ProtectedRoute>
              <OpportunitiesListPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/pricer-solar"
          element={
            <ProtectedRoute>
              <PricerSolarListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricer-solar/new"
          element={
            <ProtectedRoute>
              <PricerSolarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricer-solar/config"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.COMPANY_ADMIN, UserRole.PLATFORM_ADMIN]}>
                <PricerSolarConfigPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricer-solar/:id"
          element={
            <ProtectedRoute>
              <PricerSolarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <HelpCenterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help/curso-implementacion"
          element={
            <ProtectedRoute>
              <CursoImplementacionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tutorial"
          element={
            <ProtectedRoute>
              <TutorialPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <ProtectedRoute>
              <CustomersPage />
            </ProtectedRoute>
          }
        />
        <Route path="/users" element={<Navigate to="/mi-empresa/usuarios" replace />} />
        <Route path="/permisos" element={<Navigate to="/mi-empresa/roles" replace />} />
        <Route
          path="/configuracion/equipos"
          element={<Navigate to="/mi-empresa/equipos" replace />}
        />
        {/*
          Mi empresa (maqueta de Melanie, 25-oct): siete pestañas bajo una sola
          dirección. `/users`, `/permisos`, `/configuracion/equipos` y
          `/configuracion/aprobaciones` llevan a su pestaña; el detalle de un
          equipo (líneas de negocio, coberturas) sigue en su ruta.
        */}
        <Route
          path="/mi-empresa"
          element={
            <ProtectedRoute>
              <MiEmpresaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mi-empresa/:seccion"
          element={
            <ProtectedRoute>
              <MiEmpresaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/configuracion/aprobaciones"
          element={<Navigate to="/mi-empresa/aprobaciones" replace />}
        />
        <Route
          path="/configuracion/equipos/:id"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.COMPANY_ADMIN, UserRole.PLATFORM_ADMIN]}>
                <EquipoDetailPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-status"
          element={
            <ProtectedRoute>
              <ProjectStatusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.COMPANY_ADMIN, UserRole.PLATFORM_ADMIN]}>
                <SettingsPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/developer"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.COMPANY_ADMIN, UserRole.PLATFORM_ADMIN]}>
                <ApiDocsPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/claves-api"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.COMPANY_ADMIN, UserRole.PLATFORM_ADMIN]}>
                <ApiKeysPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integracion-api"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.COMPANY_ADMIN, UserRole.PLATFORM_ADMIN]}>
                <IntegrationPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.COMPANY_ADMIN, UserRole.PLATFORM_ADMIN]}>
                <IntegrationsAdminPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-intelligence"
          element={
            <ProtectedRoute>
              <AiIntelligencePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-productivity"
          element={
            <ProtectedRoute>
              <AiProductivityPage />
            </ProtectedRoute>
          }
        />
        {/*
            Compite reemplaza a Precios Dinámicos: la dirección vieja lleva a la vista
            equivalente (respeta la pestaña que el store haya dejado elegida, así
            «ver alertas» o «productos de esta tienda» siguen llevando a su lugar).
          */}
        <Route
          path="/dynamic-pricing"
          element={
            <ProtectedRoute>
              <RedirigirPreciosDinamicos />
            </ProtectedRoute>
          }
        />
        {/*
            El panel de identificación de una carga manual (spec 199 §4). Es una
            pantalla propia y no una solapa: con 200 filas nadie termina de
            confirmar en una sentada, y hay que poder salir y volver.
          */}
        <Route
          path="/identificacion/:jobId"
          element={
            <ProtectedRoute>
              {/* Pantalla sin AppLayout: el tema de Compite la envuelve entera. */}
              <TemaCompite>
                <IdentificacionPage />
              </TemaCompite>
            </ProtectedRoute>
          }
        />
        {/*
            L1 (vault 274): revisión de vínculos de competencia. Pantalla propia:
            son cientos y se revisan en varias sentadas.
          */}
        <Route
          path="/compite/competencia"
          element={
            <ProtectedRoute>
              <Suspense fallback={<CargandoLaPantalla />}>
                <CompetenciaPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/compite/analitica"
          element={
            <ProtectedRoute>
              <Suspense fallback={<CargandoLaPantalla />}>
                <AnaliticaPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/compite/productos"
          element={
            <ProtectedRoute>
              <Suspense fallback={<CargandoLaPantalla />}>
                <ProductosPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/compite/monitoreo"
          element={
            <ProtectedRoute>
              <Suspense fallback={<CargandoLaPantalla />}>
                <MonitoreoPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* «Revisar vínculos» vive ahora en Compite › Productos › Vincular (sólo admin). */}
        <Route
          path="/revisar-vinculos"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.COMPANY_ADMIN, UserRole.PLATFORM_ADMIN]}>
                <Navigate to="/compite/productos?tab=vincular&modo=revisar" replace />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/materials"
          element={
            <ProtectedRoute>
              <MaterialsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/approvals"
          element={
            <ProtectedRoute>
              <ApprovalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trade/tarifario"
          element={
            <ProtectedRoute>
              <TarifarioPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trade/cotizador"
          element={
            <ProtectedRoute>
              <CotizadorPage />
            </ProtectedRoute>
          }
        />
        {/*
            Aduana se fusionó con el Asistente Arancelario (2026-08-12).

            Eran dos pantallas que parecían lo mismo: las dos buscaban una
            partida. Pero ninguna liquidaba completa — la de Aduana cobraba el
            ICE y aplicaba el tope del flete aéreo pero no mostraba bolivianos ni
            los avisos; la de la ficha mostraba todo eso pero liquidaba SIN ICE y
            SIN el tope. Y para saber si una mercadería estaba prohibida había que
            buscar la misma partida otra vez, en la otra pantalla.

            Ahora la liquidación vive en la pestaña Tributos de la ficha, sobre el
            endpoint unificado. Esta URL redirige **conservando el query string**
            porque la ficha viaja en `?partida=`, y esos enlaces se comparten.
          */}
        <Route path="/trade/aduana" element={<RedirigirAlAsistente />} />
        {/*
          Comercia en tres pasos (maqueta de Melanie, reunión del 27-oct):
          Producto → Logística (origen y destino · carga · rutas) → Costo
          nacionalizado. La portada vieja («Visión general») se fue con el
          seguimiento operativo: `/comercia` abre el primer paso.
        */}
        <Route path="/comercia" element={<Navigate to="/comercia/productos" replace />} />
        {[
          '/comercia/productos',
          '/comercia/productos/proforma',
          '/comercia/productos/asistente',
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute menuKey="import-tracking">
                <ComerciaProductosPage />
              </ProtectedRoute>
            }
          />
        ))}
        <Route
          path="/comercia/logistica"
          element={<Navigate to="/comercia/logistica/origen-destino" replace />}
        />
        <Route
          path="/comercia/logistica/origen-destino"
          element={
            <ProtectedRoute menuKey="import-tracking">
              <OrigenDestinoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/comercia/logistica/carga"
          element={
            <ProtectedRoute menuKey="import-tracking">
              <CargaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/comercia/logistica/rutas"
          element={
            <ProtectedRoute menuKey="import-tracking">
              <RutasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/comercia/costo-nacionalizado"
          element={
            <ProtectedRoute menuKey="import-tracking">
              <CostoNacionalizadoPage />
            </ProtectedRoute>
          }
        />

        {/*
            Cada sección de Trade Intelligence tiene su URL.

            Antes las cinco vivían en `/importaciones?tab=…`: el enlace no decía
            dónde estabas, y al abrir una cotización la URL seguía diciendo
            "importaciones". Ahora `/trade/cotizaciones/COT-…` se puede pegar en
            un chat y quien lo abre llega a donde vos estabas.

            La página es la misma para las cuatro; lo que cambia es la sección,
            que ahora viene por prop y no por query string.
          */}
        {(
          [
            ['/trade/cotizaciones', 'quotes'],
            ['/trade/embarques', 'orders'],
            ['/trade/contenedores', 'availability'],
            ['/trade/indicadores', 'dashboard'],
          ] as const
        ).map(([path, section]) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute>
                <ImportOrdersPage section={section} />
              </ProtectedRoute>
            }
          />
        ))}

        {/* El detalle se abre desde las dos listas y conserva de dónde vino. */}
        {['/trade/cotizaciones/:id', '/trade/embarques/:id'].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute>
                <ImportOrderDetailPage />
              </ProtectedRoute>
            }
          />
        ))}
        <Route
          path="/trade/embarques/:orderId/contenedores/:containerId"
          element={
            <ProtectedRoute>
              <ContainerDetailPage />
            </ProtectedRoute>
          }
        />

        {/*
            Las rutas viejas siguen funcionando. Hay enlaces guardados y órdenes
            compartidas por mail apuntando acá: romperlos sería el clásico
            "movimos todo y perdiste tus favoritos".
          */}
        <Route path="/importaciones" element={<LegacyImportRedirect />} />
        <Route path="/importaciones/:id" element={<LegacyOrderRedirect />} />
        <Route
          path="/importaciones/:orderId/contenedores/:containerId"
          element={<LegacyContainerRedirect />}
        />
        <Route
          path="/precio-inteligente"
          element={
            <ProtectedRoute>
              <PrecioInteligenteORedirigir />
            </ProtectedRoute>
          }
        />
        {/* Compite (rediseño del 14-oct): Decisiones es el día a día; Estrategia,
              la configuración. Mismos permisos que Estrategias de Mercado
              (`route-modules.ts`): el módulo `data-import`, y Estrategia abre
              también con ver reglas porque Reglas y Productos viven ahí. */}
        <Route path="/compite" element={<Navigate to="/compite/decisiones" replace />} />
        <Route
          path="/compite/decisiones"
          element={
            <ProtectedRoute>
              <DecisionesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/compite/decisiones/revisar"
          element={
            <ProtectedRoute>
              <RevisarPreciosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/compite/estrategia"
          element={
            <ProtectedRoute>
              <EstrategiaPage />
            </ProtectedRoute>
          }
        />
        {/* Plan 3 (épica H): Reglas vive dentro de Estrategias y Descuentos salió del
              menú (decisión del 22-sep). Las direcciones viejas siguen andando. */}
        <Route path="/pricing-discounts" element={<Navigate to="/precio-inteligente" replace />} />
        <Route path="/pricing-rules" element={<RedirigirAReglas />} />
        <Route
          path="/tiendas-conectadas"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.COMPANY_ADMIN, UserRole.PLATFORM_ADMIN]}>
                <FueraDeLaDemo>
                  <StoreConnectionsPage />
                </FueraDeLaDemo>
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/historial-push"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.COMPANY_ADMIN, UserRole.PLATFORM_ADMIN]}>
                <FueraDeLaDemo>
                  <PushHistoryPage />
                </FueraDeLaDemo>
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/contracts"
          element={
            <ProtectedRoute>
              <ContractsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contracts/new"
          element={
            <ProtectedRoute>
              <ContractEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contracts/:id"
          element={
            <ProtectedRoute>
              <ContractDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contracts/:id/edit"
          element={
            <ProtectedRoute>
              <ContractEditPage />
            </ProtectedRoute>
          }
        />
        {/* «Cargar Datos» salió del menú (22-sep): la carga vive en Precios Dinámicos. */}
        <Route path="/data-import" element={<Navigate to="/compite/productos" replace />} />
        <Route path="/cargar-datos" element={<Navigate to="/compite/productos" replace />} />

        {/* ══ Plataforma · DATEC ══════════════════════════════════════════════
              Fuera del portal de toda empresa: `plataforma` es un slug reservado, así
              que `resolverPortalDeLaUrl` devuelve `{ tipo: 'publico' }` y el
              `BrowserRouter` queda sin `basename`. Ese es el punto del frente: el panel
              que administra a TODAS no puede vivir adentro de UNA.
              ═══════════════════════════════════════════════════════════════════ */}
        <Route
          path="/plataforma"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminDashboardPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/comparacion"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminComparacionPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/empresas"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminTenantsPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/empresas/:id"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminTenantDetailPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        {/* Plan 297 (S2): el catálogo de tiendas de Prixia. */}
        <Route
          path="/plataforma/tiendas"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminTiendasPrixiaPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/tiendas/:id"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminTiendaPrixiaPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/lecturas"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminLecturasPrixiaPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/invitaciones"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminInvitacionesDemoPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        {/* Landing nueva (vault 346): lo que llega de las páginas públicas (Mini Prixia y Contacto). */}
        <Route
          path="/plataforma/solicitudes"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <SolicitudesPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        {/* El detalle va ANTES que el listado no hace falta —son rutas distintas—
              pero sí que exista: sin él, las filas del listado no llevaban a ningún lado. */}
        <Route
          path="/plataforma/personas/:id"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminUserDetailPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/personas"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminPersonasPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/planes"
          element={
            <ProtectedRoute>
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminPlansPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/configuracion"
          element={
            <ProtectedRoute>
              {/*
                  🔴 Acá entraba también `COMPANY_ADMIN`, heredado de cuando esta
                  pantalla vivía en `/admin/*` adentro del portal. Con la mudanza
                  dejó de ser una inconsistencia y pasó a ser una fuga de plano:
                  un admin de una empresa cliente que escribiera la dirección se
                  encontraba con la `BarraPlataforma` entera —«Plataforma · DATEC»
                  y las siete secciones del superadmin—. `/plataforma/*` es de la
                  plataforma, sin excepciones.

                  ⚠️ El techo del servidor para estos ajustes es otro asunto, y
                  queda como está a propósito: cambiar permisos que ya corren en
                  producción al final de una rama que vino a otra cosa es cómo se
                  rompen cosas de noche.
                */}
              <RequireRole role={UserRole.PLATFORM_ADMIN}>
                <AdminPlatformSettingsPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plataforma/bugs"
          element={
            <ProtectedRoute>
              <RequireRole role={[UserRole.PLATFORM_ADMIN]}>
                <AdminBugReportsPage />
              </RequireRole>
            </ProtectedRoute>
          }
        />

        {/* ══ Las direcciones viejas ══════════════════════════════════════════
              Hay enlaces guardados y pestañas abiertas apuntando a `/admin/*`. Se
              redirigen en vez de romperse. `replace` para que el botón "atrás" no
              devuelva a la dirección vieja y rebote.
              ═══════════════════════════════════════════════════════════════════ */}
        <Route path="/admin" element={<Navigate to="/plataforma" replace />} />
        <Route path="/admin/tenants" element={<Navigate to="/plataforma/empresas" replace />} />
        <Route path="/admin/tenants/:id" element={<RedirigirConId a="/plataforma/empresas" />} />
        <Route path="/admin/users" element={<Navigate to="/plataforma/personas" replace />} />
        <Route path="/admin/users/:id" element={<RedirigirConId a="/plataforma/personas" />} />
        <Route
          path="/admin/comparacion"
          element={<Navigate to="/plataforma/comparacion" replace />}
        />
        <Route path="/admin/plans" element={<Navigate to="/plataforma/planes" replace />} />
        <Route
          path="/admin/platform"
          element={<Navigate to="/plataforma/configuracion" replace />}
        />
        <Route path="/admin/bug-reports" element={<Navigate to="/plataforma/bugs" replace />} />

        {/*
            Landing — o el dashboard, si la URL trae empresa.

            🔴 Con el basename de la empresa la ruta que queda es `/`, asi que
            `prixia.datec.com.bo/datec-srl` caia en la landing de marketing y se
            veia como una pagina vacia. Encontrado en produccion el 7-sep.
          */}
        <Route
          path="/"
          element={enEmpresa ? <Navigate to={RAIZ_DE_LA_EMPRESA} replace /> : <LandingPage />}
        />

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </MarcoDeLaApp>
  );
}

// ── Root ─────────────────────────────────────────────────────────

export function AppRouter() {
  /*
    El `basename` sale de la URL, no de la sesión: así la URL manda y dos
    pestañas con dos empresas distintas no se pisan. Y como React Router le
    antepone el basename a cada `to` y a cada `navigate`, los ~300 lugares que
    navegan con ruta absoluta siguen andando sin tocarlos.
  */
  /** Con que URL se ABRIO la pagina. Se lee una sola vez, antes de que nadie navegue. */
  const [entrada] = useState(() => ({
    ruta: window.location.pathname,
    busqueda: window.location.search,
    hash: window.location.hash,
  }));

  const portal = resolverPortalDeLaUrl(window.location.pathname);
  const basename = portal.tipo === 'empresa' ? `/${portal.slug}` : undefined;

  /*
    🔴 Entrar por la RAIZ dejaba la sesion sin empresa en la URL, para siempre.

    `resolverPortalDeLaUrl` corre una sola vez, aca arriba. Entrando por
    `prixia.datec.com.bo`, el basename queda sin definir y toda navegacion
    interna de ahi en adelante es sin prefijo: la aplicacion funciona y muestra
    datos reales, pero la direccion ya no dice de que empresa son. Eso anula la
    razon de ser de poner el slug en la ruta, y deja a la persona sin saber
    donde esta. Encontrado en produccion el 7-sep.

    Va con `location.replace` y no con `navigate`: hace falta una carga entera
    para que el basename se resuelva de nuevo. Con `navigate` la URL cambiaria y
    el basename seguiria sin empresa, que es exactamente el bug.
  */
  useEffect(() => {
    /*
      🔴 La ruta se toma del PRIMER render (`entrada`, arriba), no de
      `window.location` acá adentro. Los efectos de los hijos corren ANTES que
      los del padre: para cuando este efecto se ejecuta, un hijo ya navego a
      `/dashboard` y preguntar "¿estoy en la raiz?" da que no. Asi fallo el
      primer intento de este arreglo, y lo encontro el navegador contra dev —
      los unitarios no, porque llamaban la funcion directo.
    */
    const destino = destinoDeLaRaiz({
      autenticado: useAuthStore.getState().isAuthenticated,
      ultimaEmpresa: ultimoPortalActivo(),
      ruta: entrada.ruta,
      busqueda: entrada.busqueda,
      hash: entrada.hash,
    });
    if (destino) window.location.replace(destino);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BrowserRouter basename={basename}>
      <PortalGuard>
        <div style={{ position: 'relative', minHeight: '100vh' }}>
          <AnimatedRoutes enEmpresa={portal.tipo === 'empresa'} />
        </div>
      </PortalGuard>
    </BrowserRouter>
  );
}

export default AppRouter;
